import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ref,
  get,
  remove,
  onValue,
  update,
  push,
  set,
  serverTimestamp,
} from "firebase/database";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { auth, database, firestore, storage } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PieChart,
  XAxis,
  YAxis,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  X,
  User,
  Phone,
  MapPin,
  Hash,
  Calendar,
  TrendingUp,
  Milk,
  DollarSign,
  Download,
  Image,
} from "lucide-react";
import {
  buildB2CMonthlySeries,
  getTransactionAmount,
  getTransactionDate,
  getTransactionTime,
  normalizePhone,
} from "./farmerB2CHelpers";
import {
  buildDailyMilkChartData,
  buildMilkPayoutBreakdown,
  detectMilkRecordAnomalies,
  getFarmerCostPerKg,
} from "./farmerViewModalHelpers";
import { API_BASE_URL } from "../../../utils/apiBase";
import { fileToBase64 } from "./milkJournals/utils";
import FarmerBalanceAdjustmentModal from "./FarmerBalanceAdjustmentModal";
import { logRecordAuditEvent } from "../../../utils/recordAuditLog";
import { maskPhoneNumber, maskIdentificationNumber } from "../../../utils/dataMasking";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MAX_PHONE_LOOKUP_LIMIT =
  Number(process.env.REACT_APP_B2C_PHONE_LIMIT) || 200;

const DayCell = ({ day, value, isAnomaly }) => {
  const numValue = parseFloat(value);
  const isValid = !isNaN(numValue) && value !== "";

  return (
    <div
      className={`p-2 border rounded shadow-sm text-sm ${
        isAnomaly ? "bg-red-50 border-red-200" : "bg-white"
      }`}
    >
      <div className="text-xs text-gray-400 mb-1">Day {day}</div>
      <div className={`font-semibold ${isAnomaly ? "text-red-700" : "text-green-700"}`}>
        {isValid ? `${numValue.toFixed(1)} Kgs` : "—"}
      </div>
      {isAnomaly ? (
        <div className="text-[11px] text-red-600 mt-1">Anomaly suspected</div>
      ) : null}
    </div>
  );
};

const FarmerViewModal = ({ farmer, companyName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [farmerData, setFarmerData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [milkData, setMilkData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [advanceData, setAdvanceData] = useState([]);
  const [b2cPayments, setB2cPayments] = useState([]);
  const [b2cLoading, setB2cLoading] = useState(false);
  const [connectedFarmerInfo, setConnectedFarmerInfo] = useState(null); // stores name and email from Firestore
  const [loadingConnectedInfo, setLoadingConnectedInfo] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showModeModal, setShowModeModal] = useState(false);
  const [showBalanceAdjustmentModal, setShowBalanceAdjustmentModal] =
    useState(false);
  const [modeSelection, setModeSelection] = useState(farmer?.mode || "KCB");
  const [modeKey, setModeKey] = useState("");
  const [savingMode, setSavingMode] = useState(false);
  const [showMilkCardModal, setShowMilkCardModal] = useState(false);
  const [milkCardYear, setMilkCardYear] = useState(() => new Date().getFullYear());
  const [milkCardMonthIndex, setMilkCardMonthIndex] = useState(() => new Date().getMonth());
  const [milkCardFront, setMilkCardFront] = useState(null);
  const [milkCardBack, setMilkCardBack] = useState(null);
  const [milkCardUploadProgress, setMilkCardUploadProgress] = useState({});
  const [milkCardSaving, setMilkCardSaving] = useState(false);
  const [milkCardChecks, setMilkCardChecks] = useState({
    front: null,
    back: null,
  });

  // Calendar view state
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(
    today.getMonth()
  ); // 0-11
  const [dailyRecords, setDailyRecords] = useState({}); // { "1": "12.3", "2": "..." }
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [stats, setStats] = useState({
    totalMilk: 0,
    totalPayments: 0,
    totalAdvance: 0,
    averageMilk: 0,
  });

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  // Build firebase path for the selected month in calendar view
  const monthPath = useMemo(() => {
    if (!companyName || !farmer?.otherName) return null;
    const year = String(selectedYear);
    const month = String(selectedMonthIndex + 1); // use 1-based month in DB
    return `Companies/${companyName}/FarmerRecords/${farmer.otherName}/${year}/${month}`;
  }, [companyName, farmer, selectedYear, selectedMonthIndex]);

  const getUserRoleAndPermissions = useCallback(async (uid, company) => {
    try {
      const userDoc = await getDoc(doc(firestore, "users", uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.companyName) {
          const employeeDoc = await getDoc(
            doc(firestore, "companies", data.companyName, "employees", uid)
          );
          if (employeeDoc.exists()) {
            return employeeDoc.data();
          }
        }
        return data;
      }
      const realtimeSnap = await get(ref(database, `Users/${uid}`));
      if (realtimeSnap.exists()) {
        return realtimeSnap.val();
      }
    } catch (error) {
      console.error("Error fetching user role for delete:", error);
    }
    return null;
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setUserRole(null);
        return;
      }
      const roleData = await getUserRoleAndPermissions(user.uid, companyName);
      setUserRole(roleData?.role || roleData?.accountType || null);
    });
    return () => unsubscribe();
  }, [companyName, getUserRoleAndPermissions]);

  const fetchFarmerDetails = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch complete farmer details
      const farmerRef = ref(
        database,
        `Companies/${companyName}/ValidatedFarmers/${farmer.otherName}`
      );
      const farmerSnap = await get(farmerRef);
      if (farmerSnap.exists()) {
        setFarmerData(farmerSnap.val());
      }

      // Fetch milk records (last 30 days)
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().toLocaleString("en-US", {
        month: "long",
      });

      const milkRecordsRef = ref(
        database,
        `Companies/${companyName}/FarmerRecords/${farmer.otherName}/${currentYear}/${currentMonth}`
      );
      const milkSnap = await get(milkRecordsRef);

      const milkRecords = [];
      let totalMilkQty = 0;

      if (milkSnap.exists()) {
        const data = milkSnap.val();
        Object.entries(data).forEach(([day, value]) => {
          // Milk records are stored as string/number values at day keys (e.g., "1", "2", "15")
          // Other nested objects like "Payments" or "AdvanceTransactions" are objects
          if (typeof value === "string" || typeof value === "number") {
            const qty = parseFloat(value) || 0;
            totalMilkQty += qty;
            milkRecords.push({
              day: day,
              quantity: qty,
            });
          }
        });
      }

      // Sort by day
      milkRecords.sort((a, b) => parseInt(a.day) - parseInt(b.day));
      setMilkData(milkRecords);

      // Fetch payments
      const paymentsRef = ref(
        database,
        `Companies/${companyName}/FarmerRecords/${farmer.otherName}/Payments/${currentYear}/${currentMonth}`
      );
      const paymentsSnap = await get(paymentsRef);

      const payments = [];
      let totalPaymentAmount = 0;

      if (paymentsSnap.exists()) {
        const data = paymentsSnap.val();
        Object.values(data).forEach((payment) => {
          const amount = parseFloat(payment.Amount) || 0;
          totalPaymentAmount += amount;
          payments.push({
            id: payment.Id,
            amount: amount,
            date: payment.TransactionDate,
            status: payment.Status,
            mode: payment.Mode,
          });
        });
      }

      setPaymentData(payments);

      // Fetch advance transactions
      const advanceRef = ref(
        database,
        `Companies/${companyName}/FarmerRecords/${farmer.otherName}/AdvanceTransactions/${currentYear}/${currentMonth}`
      );
      const advanceSnap = await get(advanceRef);

      const advances = [];
      let totalAdvanceAmount = 0;

      if (advanceSnap.exists()) {
        const data = advanceSnap.val();
        Object.values(data).forEach((adv) => {
          const amount = parseFloat(adv.Amount) || 0;
          // Track total advance given (for statistics)
          if (adv.TransactionType === "add") {
            totalAdvanceAmount += amount;
          } else if (adv.TransactionType === "deduct") {
            totalAdvanceAmount -= amount;
          }
          advances.push({
            id: adv.Id,
            amount: amount,
            type: adv.TransactionType,
            date: adv.TransactionDate,
            details: adv.Details,
          });
        });
      }

      setAdvanceData(advances);

      // Calculate stats
      setStats({
        totalMilk: totalMilkQty,
        totalPayments: totalPaymentAmount,
        totalAdvance: totalAdvanceAmount,
        averageMilk:
          milkRecords.length > 0 ? totalMilkQty / milkRecords.length : 0,
      });

      // If there's a LinkedFarmer (UID), fetch the user info from Firestore
      if (farmerSnap.exists()) {
        const farmerInfo = farmerSnap.val();
        if (farmerInfo.LinkedFarmer) {
          setLoadingConnectedInfo(true);
          try {
            const userDocRef = doc(
              firestore,
              "users",
              farmerInfo.LinkedFarmer
            );
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              setConnectedFarmerInfo({
                uid: farmerInfo.LinkedFarmer,
                name: userData.fullName || userData.name || "Unknown",
                email: userData.email || "No email",
              });
            } else {
              setConnectedFarmerInfo({
                uid: farmerInfo.LinkedFarmer,
                name: "User not found",
                email: "—",
              });
            }
          } catch (error) {
            console.error("Error fetching connected farmer info:", error);
            setConnectedFarmerInfo({
              uid: farmerInfo.LinkedFarmer,
              name: "Error loading",
              email: "—",
            });
          } finally {
            setLoadingConnectedInfo(false);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching farmer details:", error);
      toast.error("Failed to load farmer details");
    } finally {
      setLoading(false);
    }
  }, [farmer, companyName]);

  useEffect(() => {
    fetchFarmerDetails();
  }, [fetchFarmerDetails]);

  const fetchB2CPayments = useCallback(async () => {
    const phoneRaw = farmerData?.PhoneNumber || farmer?.phone;
    const phone = normalizePhone(phoneRaw);
    const currentYear = String(new Date().getFullYear());

    if (!firestore || !phone) {
      setB2cPayments([]);
      setB2cLoading(false);
      return;
    }

    setB2cLoading(true);
    try {
      // Build month list from January (0) through current month for YTD queries
      const currentMonthIndex = new Date().getMonth(); // 0-based
      const months = Array.from({ length: currentMonthIndex + 1 }, (_, idx) =>
        String(idx + 1).padStart(2, "0")
      );
      const allRecords = [];

      const monthSnaps = await Promise.all(
        months.map((month) => {
          const colRef = collection(
            firestore,
            "b2c_transactions_by_phone",
            phone,
            currentYear,
            month,
            "tx"
          );
          const q = query(
            colRef,
            orderBy("processedAt", "desc"),
            limit(MAX_PHONE_LOOKUP_LIMIT)
          );
          return getDocs(q);
        })
      );

      monthSnaps.forEach((snap) => {
        snap.forEach((docSnap) => {
          allRecords.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });
      });
      allRecords.sort((a, b) => getTransactionTime(b) - getTransactionTime(a));
      setB2cPayments(allRecords);
    } catch (error) {
      console.error("Error loading B2C payments", error);
      setB2cPayments([]);
    } finally {
      setB2cLoading(false);
    }
  }, [farmer?.phone, farmerData?.PhoneNumber, firestore]);

  useEffect(() => {
    fetchB2CPayments();
  }, [fetchB2CPayments]);

  useEffect(() => {
    if (farmerData?.Mode) {
      setModeSelection(farmerData.Mode);
    } else if (farmer?.mode) {
      setModeSelection(farmer.mode);
    }
  }, [farmer?.mode, farmerData?.Mode]);

  // Subscribe to realtime updates for the selected month in calendar view
  useEffect(() => {
    if (!monthPath || !database) {
      setDailyRecords({});
      setCalendarLoading(false);
      return;
    }

    setCalendarLoading(true);
    const calendarRef = ref(database, monthPath);

    const handleSnapshot = (snap) => {
      const val = snap.val() || {};
      // Filter out non-day entries by checking if key is a valid day number (1-31)
      const filteredRecords = {};
      Object.entries(val).forEach(([key, value]) => {
        const dayNum = parseInt(key, 10);
        if (
          !isNaN(dayNum) &&
          dayNum >= 1 &&
          dayNum <= 31 &&
          (typeof value === "string" || typeof value === "number")
        ) {
          filteredRecords[key] = value;
        }
      });
      setDailyRecords(filteredRecords);
      setCalendarLoading(false);
    };

    const unsubscribe = onValue(calendarRef, handleSnapshot, (err) => {
      console.error("Calendar realtime error:", err);
      setCalendarLoading(false);
    });

    return () => {
      // Cleanup: unsubscribe from Firebase listener
      unsubscribe();
    };
  }, [monthPath]);

  const handleDetachConnectedFarmer = async () => {
    if (!connectedFarmerInfo) return;

    const confirmed = window.confirm(
      `Are you sure you want to detach ${connectedFarmerInfo.name} from viewing this farmer's data?`
    );

    if (!confirmed) return;

    try {
      const linkedFarmerPath = `Companies/${companyName}/ValidatedFarmers/${farmer.otherName}/LinkedFarmer`;
      await remove(ref(database, linkedFarmerPath));
      toast.success("Connected farmer detached successfully");
      setConnectedFarmerInfo(null);
    } catch (error) {
      console.error("Error detaching connected farmer:", error);
      toast.error("Failed to detach connected farmer");
    }
  };

  const handleDeleteFarmer = async () => {
    const confirmationTargets = [
      companyName?.toLowerCase(),
      farmer?.otherName?.toLowerCase(),
    ].filter(Boolean);

    if (!confirmationTargets.includes(deleteInput.trim().toLowerCase())) {
      toast.error(
        `Type "${farmer?.otherName}" or "${companyName}" to confirm deletion`
      );
      return;
    }

    if (userRole !== "CompanyAdmin") {
      toast.error("Only CompanyAdmin can delete farmers");
      return;
    }
    if (!companyName) {
      toast.error("Company name not available for deletion");
      return;
    }

    setDeleting(true);
    const farmerKey = farmer?.otherName;
    if (!farmerKey) {
      toast.error("Missing farmer identifier");
      setDeleting(false);
      return;
    }
    try {
      const removalPaths = [
        `Companies/${companyName}/ValidatedFarmers/${farmerKey}`,
        `Companies/${companyName}/AIMilkValidatedFarmers/${farmerKey}`,
        `Companies/${companyName}/NewValidatedFarmers/${farmerKey}`,
        `Companies/${companyName}/FarmerRecords/${farmerKey}`,
      ];

      await Promise.all(removalPaths.map((path) => remove(ref(database, path))));
      await logRecordAuditEvent({
        action: "delete",
        entityType: "farmer",
        companyName,
        recordId: farmerKey,
        sourceComponent: "FarmerViewModal",
        oldData: farmer,
      });
      toast.success("Farmer deleted successfully");
      setShowDeleteConfirm(false);
      setDeleteInput("");
      onClose();
    } catch (error) {
      console.error("Error deleting farmer:", error);
      toast.error("Failed to delete farmer");
    } finally {
      setDeleting(false);
    }
  };

  const handleModeSave = async () => {
    if (userRole !== "CompanyAdmin") {
      toast.error("Only CompanyAdmin can change payment mode");
      return;
    }

    // Require exact value: A1B2C3/{otherName}
    const expectedKey = `A1B2C3/${farmer?.otherName || ""}`;
    if (modeKey.trim() !== expectedKey) {
      toast.error(`Enter the correct authorization key: ${expectedKey}`);
      return;
    }

    if (!companyName || !farmer?.otherName) {
      toast.error("Missing company or farmer information");
      return;
    }

    setSavingMode(true);
    try {
      const modePaths = [
        `Companies/${companyName}/ValidatedFarmers/${farmer.otherName}`,
        `Companies/${companyName}/AIMilkValidatedFarmers/${farmer.otherName}`,
        `Companies/${companyName}/NewValidatedFarmers/${farmer.otherName}`,
      ];

      await Promise.all(
        modePaths.map((path) => update(ref(database, path), { Mode: modeSelection }))
      );

      setFarmerData((prev) =>
        prev ? { ...prev, Mode: modeSelection } : prev
      );

      toast.success("Payment mode updated successfully");
      setShowModeModal(false);
      setModeKey("");
    } catch (error) {
      console.error("Error updating payment mode:", error);
      toast.error("Failed to update payment mode");
    } finally {
      setSavingMode(false);
    }
  };

  // Calendar view totals
  const calendarTotals = useMemo(() => {
    let firstHalf = 0;
    let secondHalf = 0;
    let monthly = 0;

    const actualDays = new Date(
      selectedYear,
      selectedMonthIndex + 1,
      0
    ).getDate();
    for (let d = 1; d <= actualDays; d++) {
      const key = String(d);
      const raw = dailyRecords[key];
      const num = parseFloat(raw);
      if (!isNaN(num)) {
        monthly += num;
        if (d <= 15) firstHalf += num;
        else secondHalf += num;
      }
    }
    return { firstHalf, secondHalf, monthly };
  }, [dailyRecords, selectedYear, selectedMonthIndex]);

  const yearOptions = useMemo(() => {
    const thisYear = new Date().getFullYear();
    // show range currentYear-2 .. currentYear+1
    return [thisYear - 2, thisYear - 1, thisYear, thisYear + 1];
  }, []);

  // Calculate actual days in the selected month
  const daysInMonth = useMemo(() => {
    return new Date(selectedYear, selectedMonthIndex + 1, 0).getDate();
  }, [selectedYear, selectedMonthIndex]);

  // Calculate average daily milk from calendar data
  const avgDailyMilkFromCalendar = useMemo(() => {
    const daysWithData = Object.keys(dailyRecords).filter((key) => {
      const num = parseFloat(dailyRecords[key]);
      return !isNaN(num) && num > 0;
    }).length;
    return daysWithData > 0 ? calendarTotals.monthly / daysWithData : 0;
  }, [dailyRecords, calendarTotals.monthly]);

  const monthlyMilkChartData = useMemo(() => {
    return buildDailyMilkChartData(dailyRecords, daysInMonth);
  }, [dailyRecords, daysInMonth]);

  const milkAnomalyStats = useMemo(() => {
    return detectMilkRecordAnomalies(dailyRecords, daysInMonth);
  }, [dailyRecords, daysInMonth]);

  const anomalyDaySet = useMemo(
    () => new Set(milkAnomalyStats.anomalyDays),
    [milkAnomalyStats.anomalyDays]
  );

  const milkPayout = useMemo(() => {
    const unitCost = getFarmerCostPerKg(farmerData, farmer);
    return buildMilkPayoutBreakdown(calendarTotals, unitCost);
  }, [calendarTotals, farmerData, farmer]);

  const milkCardPaths = useMemo(() => {
    if (!companyName || !farmer?.otherName) return null;
    const year = String(milkCardYear);
    const month = String(milkCardMonthIndex + 1).padStart(2, "0");
    return {
      storageBase: `milk-card-journal/${companyName}/${farmer.otherName}/${year}/${month}`,
      dbPath: `Companies/${companyName}/MilkCardJournals/${farmer.otherName}/${year}/${month}`,
      year,
      month,
    };
  }, [companyName, farmer?.otherName, milkCardMonthIndex, milkCardYear]);

  const handleMilkCardFileChange = (side, file) => {
    setMilkCardChecks((prev) => ({ ...prev, [side]: null }));
    if (file) {
      const allowedExt = /\.(jpe?g|png|webp)$/i;
      if (!allowedExt.test(file.name)) {
        toast.error("Please upload JPG, JPEG, PNG, or WEBP images only");
        if (side === "front") setMilkCardFront(null);
        else setMilkCardBack(null);
        return;
      }
    }
    if (side === "front") {
      setMilkCardFront(file || null);
    } else {
      setMilkCardBack(file || null);
    }
    if (file) {
      moderateMilkCardImage(file, side);
    }
  };

  const moderateMilkCardImage = async (file, side) => {
    if (!file) return false;
    setMilkCardChecks((prev) => ({
      ...prev,
      [side]: { status: "verifying", message: "Checking content safety..." },
    }));
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Please sign in to verify images");
      }
      const idToken = await user.getIdToken();
      const base64Image = await fileToBase64(file);
      const response = await fetch(`${API_BASE_URL}/api/moderate-image/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          image_base64: base64Image,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Content check failed");
      }
      const safe = result.is_safe !== false;
      setMilkCardChecks((prev) => ({
        ...prev,
        [side]: {
          status: safe ? "passed" : "failed",
          message:
            result.message ||
            (safe ? "Image passed safety check" : "Image flagged"),
          issues: result.safety_issues || [],
        },
      }));
      if (safe) {
        toast.success(`${side === "front" ? "Front" : "Back"} image verified`);
      } else {
        toast.error(result.message || "Image failed content check");
      }
      return safe;
    } catch (error) {
      console.error("Milk card moderation error:", error);
      setMilkCardChecks((prev) => ({
        ...prev,
        [side]: {
          status: "error",
          message: error.message || "Content check failed",
        },
      }));
      toast.error(`Content check failed for ${side} image`);
      return false;
    }
  };

  const generateUploadId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    const getByte = () => {
      if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        const buf = new Uint8Array(1);
        crypto.getRandomValues(buf);
        return buf[0];
      }
      return Math.floor(Math.random() * 256);
    };
    const template = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
    return template.replace(/[xy]/g, (c) => {
      const r = getByte() & 0x0f;
      if (c === "x") return r.toString(16);
      return ((r & 0x3) | 0x8).toString(16);
    });
  };

  const uploadMilkCardImage = async (file, side) => {
    if (!storage) {
      throw new Error("Firebase storage is not initialized. Please try again.");
    }
    if (!milkCardPaths) {
      throw new Error("Missing company or farmer information for upload.");
    }
    let safeName = file.name
      .replace(/[/\\]/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "_")
      .replace(/\.\.+/g, ".")
      .replace(/^\.+/, "")
      .replace(/_+/g, "_");
    if (!safeName) {
      safeName = "upload";
    }
    const uniqueId = generateUploadId();
    const filename = `${side}-${uniqueId}-${safeName}`;
    const path = `${milkCardPaths.storageBase}/${filename}`;
    const sRef = storageRef(storage, path);
    const metadata = {
      contentType: file.type || "image/jpeg",
      customMetadata: {
        companyName,
        farmerKey: farmer?.otherName || "",
        year: milkCardPaths.year,
        month: milkCardPaths.month,
        side,
      },
    };
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(sRef, file, metadata);
      task.on(
        "state_changed",
        (snapshot) => {
          const pct = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setMilkCardUploadProgress((prev) => ({ ...prev, [side]: pct }));
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve({ url, storagePath: path });
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  };

  const handleSaveMilkCard = async () => {
    if (!milkCardFront || !milkCardBack) {
      toast.error("Please add both front and back images");
      return;
    }
    if (!milkCardPaths) {
      toast.error("Missing company or farmer information");
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      toast.error("You must be signed in to upload milk cards");
      return;
    }
    const frontStatus = milkCardChecks.front?.status;
    const backStatus = milkCardChecks.back?.status;
    if (frontStatus === "verifying" || backStatus === "verifying") {
      toast.error("Please wait for content checks to finish before saving");
      return;
    }
    if (frontStatus !== "passed" || backStatus !== "passed") {
      toast.error("Please resolve content checks for both images before saving");
      return;
    }
    setMilkCardSaving(true);
    setMilkCardUploadProgress({});
    try {
      const [frontUpload, backUpload] = await Promise.all([
        uploadMilkCardImage(milkCardFront, "front"),
        uploadMilkCardImage(milkCardBack, "back"),
      ]);
      const recordRef = push(ref(database, milkCardPaths.dbPath));
      await set(recordRef, {
        year: milkCardPaths.year,
        month: milkCardPaths.month,
        frontUrl: frontUpload.url,
        backUrl: backUpload.url,
        uploadedAt: serverTimestamp(),
        uploadedBy: user.uid,
      });
      toast.success("Milk card journal saved");
      setMilkCardFront(null);
      setMilkCardBack(null);
      setMilkCardChecks({ front: null, back: null });
      setMilkCardUploadProgress({});
      setShowMilkCardModal(false);
    } catch (error) {
      console.error("Milk card save error:", error);
      toast.error("Failed to save milk card");
    } finally {
      setMilkCardSaving(false);
    }
  };

  const b2cChartData = useMemo(
    () => buildB2CMonthlySeries(b2cPayments, new Date().getFullYear()),
    [b2cPayments]
  );

  const recentB2cPayments = useMemo(
    () => b2cPayments.slice(0, 10),
    [b2cPayments]
  );

  const farmerPhoneValue = farmerData?.PhoneNumber || farmer.phone || "";
  const normalizedFarmerPhone = normalizePhone(farmerPhoneValue);
  const displayPhone = normalizedFarmerPhone || farmerPhoneValue;

  // Sanitize filename for safe filesystem usage
  const sanitizeFilename = (name) => {
    return name.replace(/[^a-z0-9_\-]/gi, "_").replace(/_+/g, "_");
  };

  // Escape CSV values according to RFC 4180
  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // Export calendar data as CSV
  const handleExportCSV = () => {
    try {
      const rows = [["Day", "Quantity (Kgs)", "Date"]];
      for (let d = 1; d <= daysInMonth; d++) {
        const qty = dailyRecords[String(d)] || "";
        const dateStr = `${selectedYear}-${String(
          selectedMonthIndex + 1
        ).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        rows.push([d, qty, dateStr]);
      }
      const csvContent = rows
        .map((r) => r.map(escapeCsvValue).join(","))
        .join("\n");
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeFarmerName = sanitizeFilename(farmer.name);
      a.download = `milk_records_${safeFarmerName}_${MONTHS[selectedMonthIndex]}_${selectedYear}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported successfully!");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  // Export calendar data as PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Title
      doc.setFontSize(18);
      const safeFarmerName = sanitizeFilename(farmer.name);
      doc.text(`Milk Records - ${farmer.name}`, 14, 20);

      // Subtitle
      doc.setFontSize(12);
      doc.text(`${MONTHS[selectedMonthIndex]} ${selectedYear}`, 14, 30);
      doc.text(`Company: ${companyName}`, 14, 37);

      // Summary statistics
      doc.setFontSize(14);
      doc.text("Summary Statistics", 14, 50);
      doc.setFontSize(10);
      doc.text(
        `Total Milk: ${calendarTotals.monthly.toFixed(1)} Kgs`,
        14,
        58
      );
      doc.text(
        `First Half (1-15): ${calendarTotals.firstHalf.toFixed(1)} Kgs`,
        14,
        65
      );
      doc.text(
        `Second Half (16-${daysInMonth}): ${calendarTotals.secondHalf.toFixed(
          1
        )} Kgs`,
        14,
        72
      );
      doc.text(
        `Average Daily: ${avgDailyMilkFromCalendar.toFixed(1)} Kgs/day`,
        14,
        79
      );

      // Daily records table header
      doc.setFontSize(14);
      doc.text("Daily Records", 14, 95);

      // Table header
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text("Day", 14, 105);
      doc.text("Quantity (Kgs)", 60, 105);
      doc.text("Date", 120, 105);
      doc.setFont(undefined, "normal");

      // Table rows
      let y = 113;
      for (let d = 1; d <= daysInMonth; d++) {
        if (y > 270) {
          doc.addPage();
          y = 20;
          doc.setFontSize(10);
          doc.setFont(undefined, "bold");
          doc.text("Day", 14, y);
          doc.text("Quantity (Kgs)", 60, y);
          doc.text("Date", 120, y);
          doc.setFont(undefined, "normal");
          y += 8;
        }

        const qty = dailyRecords[String(d)];
        const qtyStr =
          qty !== undefined && qty !== ""
            ? parseFloat(qty).toFixed(1)
            : "—";
        const dateStr = `${selectedYear}-${String(
          selectedMonthIndex + 1
        ).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

        doc.text(String(d), 14, y);
        doc.text(qtyStr, 60, y);
        doc.text(dateStr, 120, y);
        y += 7;
      }

      doc.save(
        `milk_records_${safeFarmerName}_${MONTHS[selectedMonthIndex]}_${selectedYear}.pdf`
      );
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  if (!farmer) return null;

  // Removed milkChartData since we removed the line chart

  const summaryPieData = [
    { name: "Total Milk (Kgs)", value: stats.totalMilk },
    { name: "Total Payments", value: stats.totalPayments },
    {
      name: "Advance Balance",
      value: parseFloat(farmer.advanceBalance || 0),
    },
  ];

  const canDeleteFarmer = userRole === "CompanyAdmin";

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-6 rounded-t-lg flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">{farmer.name}</h2>
            <p className="text-cyan-100">Farmer Details & Analytics</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-cyan-800 p-2 rounded-full transition"
          >
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-700 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading farmer data...</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Farmer Information */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <User size={20} className="text-cyan-600" />
                Farmer Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <User size={16} />
                    <span className="text-sm font-medium">Full Name</span>
                  </div>
                  <p className="text-gray-800 font-semibold">
                    {farmerData?.CloudName || farmer.name}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Phone size={16} />
                    <span className="text-sm font-medium">Phone Number</span>
                  </div>
                  <p className="text-gray-800 font-semibold">
                    {userRole === "CompanyAdmin"
                      ? (farmerData?.PhoneNumber || farmer.phone)
                      : maskPhoneNumber(farmerData?.PhoneNumber || farmer.phone)}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <MapPin size={16} />
                    <span className="text-sm font-medium">Address</span>
                  </div>
                  <p className="text-gray-800 font-semibold">
                    {farmerData?.Address || farmer.address}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Hash size={16} />
                    <span className="text-sm font-medium">ID Number</span>
                  </div>
                  <p className="text-gray-800 font-semibold">
                    {farmerData?.IdentificationNumber
                      ? (userRole === "CompanyAdmin"
                          ? farmerData.IdentificationNumber
                          : maskIdentificationNumber(farmerData.IdentificationNumber))
                      : "N/A"}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Calendar size={16} />
                    <span className="text-sm font-medium">Hat Level</span>
                  </div>
                  <p className="text-gray-800 font-semibold">
                    {farmerData?.Hat || farmer.hat}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <TrendingUp size={16} />
                    <span className="text-sm font-medium">Payment Span</span>
                  </div>
                  <p className="text-gray-800 font-semibold">
                    {farmerData?.House || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Key Statistics */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Key Statistics (Selected Month -{" "}
                {MONTHS[selectedMonthIndex]} {selectedYear})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Total Milk</p>
                      <p className="text-3xl font-bold">
                        {calendarTotals.monthly.toFixed(1)}
                      </p>
                      <p className="text-green-100 text-xs">Kgs</p>
                    </div>
                    <Milk size={40} className="opacity-50" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">
                        Avg Daily Milk
                      </p>
                      <p className="text-3xl font-bold">
                        {avgDailyMilkFromCalendar.toFixed(1)}
                      </p>
                      <p className="text-blue-100 text-xs">Kgs/day</p>
                    </div>
                    <TrendingUp size={40} className="opacity-50" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white p-6 rounded-lg shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-cyan-100 text-sm">
                        Total Payments
                      </p>
                      <p className="text-3xl font-bold">
                        {stats.totalPayments.toLocaleString()}
                      </p>
                      <p className="text-cyan-100 text-xs">KES</p>
                    </div>
                    <DollarSign size={40} className="opacity-50" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">
                        Advance Balance
                      </p>
                      <p className="text-3xl font-bold">
                        {Number(
                          farmer.advanceBalance || 0
                        ).toLocaleString()}
                      </p>
                      <p className="text-purple-100 text-xs">KES</p>
                    </div>
                    <TrendingUp size={40} className="opacity-50" />
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section - Removed Milk Collection Trend */}
            <div className="grid grid-cols-1 gap-6 mb-8">
              {/* Summary Pie Chart */}
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Financial Summary
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={summaryPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) =>
                        `${name}: ${value.toFixed(0)}`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {summaryPieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Milk Records Summary Card */}
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp size={20} className="text-emerald-600" />
                  Monthly Milk Trend ({MONTHS[selectedMonthIndex]} {selectedYear})
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyMilkChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${Number(value).toFixed(1)} Kgs`, "Milk"]} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="qty"
                        stroke="#16a34a"
                        name="Milk (Kgs)"
                        dot={false}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Milk size={20} className="text-green-600" />
                  Milk Records Performance
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">
                      Total Milk
                    </div>
                    <div className="text-2xl font-bold text-green-700">
                      {calendarTotals.monthly.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Kgs this month
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">
                      Average Daily
                    </div>
                    <div className="text-2xl font-bold text-blue-700">
                      {avgDailyMilkFromCalendar.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Kgs per day
                    </div>
                  </div>
                  <div className="bg-cyan-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">
                      First Half
                    </div>
                    <div className="text-2xl font-bold text-cyan-700">
                      {calendarTotals.firstHalf.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-500">Days 1-15</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">
                      Second Half
                    </div>
                    <div className="text-2xl font-bold text-purple-700">
                      {calendarTotals.secondHalf.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Days 16-{daysInMonth}
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-4 rounded-lg border border-amber-200 bg-amber-50">
                  <div className="text-sm text-gray-600 mb-2">
                    Payment estimate at KES {milkPayout.unitCost.toFixed(2)} per Kg
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <div className="text-xs uppercase text-gray-500">First Half (1-15)</div>
                      <div className="text-lg font-semibold text-amber-700">
                        KES {milkPayout.firstHalfAmount.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-500">
                        Second Half (16-{daysInMonth})
                      </div>
                      <div className="text-lg font-semibold text-amber-700">
                        KES {milkPayout.secondHalfAmount.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-500">Total Month</div>
                      <div className="text-lg font-semibold text-amber-700">
                        KES {milkPayout.totalAmount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Export Buttons */}
                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    <Download size={18} />
                    Export CSV
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    <Download size={18} />
                    Export PDF
                  </button>
                </div>
              </div>

              {/* Monthly Calendar View Card */}
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Calendar size={20} className="text-cyan-600" />
                    Monthly Milk Records Calendar
                  </h3>
                  <div className="flex items-center gap-2">
                    <select
                      className="border rounded px-2 py-1 text-sm"
                      value={selectedMonthIndex}
                      onChange={(e) =>
                        setSelectedMonthIndex(Number(e.target.value))
                      }
                    >
                      {MONTHS.map((m, i) => (
                        <option key={m} value={i}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <select
                      className="border rounded px-2 py-1 text-sm"
                      value={selectedYear}
                      onChange={(e) =>
                        setSelectedYear(Number(e.target.value))
                      }
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        setMilkCardYear(selectedYear);
                        setMilkCardMonthIndex(selectedMonthIndex);
                        setMilkCardFront(null);
                        setMilkCardBack(null);
                        setMilkCardChecks({ front: null, back: null });
                        setMilkCardUploadProgress({});
                        setShowMilkCardModal(true);
                      }}
                      className="flex items-center gap-2 px-3 py-1 bg-cyan-600 text-white rounded hover:bg-cyan-700 text-sm"
                    >
                      <Image size={16} />
                      Add Milk Card
                    </button>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">
                      1st Half
                    </div>
                    <div className="text-lg font-bold text-green-700">
                      {calendarTotals.firstHalf.toFixed(1)} Kgs
                    </div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">
                      2nd Half
                    </div>
                    <div className="text-lg font-bold text-blue-700">
                      {calendarTotals.secondHalf.toFixed(1)} Kgs
                    </div>
                  </div>
                  <div className="bg-cyan-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">
                      Total
                    </div>
                    <div className="text-lg font-bold text-cyan-700">
                      {calendarTotals.monthly.toFixed(1)} Kgs
                    </div>
                  </div>
                </div>

                <div className="mb-4 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  {milkAnomalyStats.sampleSize < 3 ? (
                    <span>
                      Need at least 3 daily records to run anomaly analysis for this month.
                    </span>
                  ) : milkAnomalyStats.anomalyCount > 0 ? (
                    <span>
                      Statistical anomaly check flagged {milkAnomalyStats.anomalyCount} day
                      {milkAnomalyStats.anomalyCount > 1 ? "s" : ""}. Highlighted days are
                      unusually high or low compared to typical values for this month.
                    </span>
                  ) : (
                    <span>
                      Statistical anomaly check found no unusual milk records for this month.
                    </span>
                  )}
                </div>

                {/* Calendar Grid */}
                {calendarLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading calendar...
                  </div>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-2">
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const day = i + 1;
                      const raw = dailyRecords[String(day)];
                      return (
                        <DayCell
                          key={day}
                          day={day}
                          value={raw}
                          isAnomaly={anomalyDaySet.has(day)}
                        />
                      );
                    })}
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="mt-4 flex justify-between items-center">
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (selectedMonthIndex === 0) {
                          setSelectedMonthIndex(11);
                          setSelectedYear((y) => y - 1);
                        } else {
                          setSelectedMonthIndex((m) => m - 1);
                        }
                      }}
                      className="px-3 py-1 border rounded hover:bg-gray-50 text-sm"
                    >
                      Prev
                    </button>
                    <button
                      onClick={() => {
                        if (selectedMonthIndex === 11) {
                          setSelectedMonthIndex(0);
                          setSelectedYear((y) => y + 1);
                        } else {
                          setSelectedMonthIndex((m) => m + 1);
                        }
                      }}
                      className="px-3 py-1 border rounded hover:bg-gray-50 text-sm"
                    >
                      Next
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportCSV}
                      className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                      <Download size={14} />
                      CSV
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >
                      <Download size={14} />
                      PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Payments */}
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Recent Payments
                </h3>
                {paymentData.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {paymentData
                      .slice(-10)
                      .reverse()
                      .map((payment, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <p className="font-semibold text-gray-800">
                              KES{" "}
                              {payment.amount.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(
                                payment.date
                              ).toLocaleDateString()}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              payment.status === "Pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {payment.status || "Completed"}
                          </span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">
                    No payment records
                  </p>
                )}
              </div>

              {/* Recent Advance Transactions */}
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Recent Advance Transactions
                </h3>
                {advanceData.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {advanceData
                      .slice(-10)
                      .reverse()
                      .map((adv, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <p className="font-semibold text-gray-800">
                              {adv.type === "add" ? "+" : "-"} KES{" "}
                              {adv.amount.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(
                                adv.date
                              ).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-600">
                              {adv.details}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              adv.type === "add"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            {adv.type === "add" ? "Added" : "Deducted"}
                          </span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">
                    No advance transaction records
                  </p>
                )}
              </div>
            </div>

            {/* B2C Payments overview */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">
                    Recent B2C Payments
                  </h3>
                  <span className="text-xs text-gray-500">
                    {displayPhone ? `Phone: ${displayPhone}` : "Phone unavailable"}
                  </span>
                </div>
                {b2cLoading ? (
                  <div className="py-8 text-center text-gray-500">
                    Loading B2C payments...
                  </div>
                ) : recentB2cPayments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-600 border-b">
                          <th className="py-2 pr-4">Date</th>
                          <th className="py-2 pr-4">Amount (KES)</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Ref</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentB2cPayments.map((tx, idx) => {
                          const amount = getTransactionAmount(tx);
                          const date = getTransactionDate(tx);
                          const statusValue =
                            tx.status ||
                            tx.transactionStatus ||
                            tx.ResultDesc ||
                            tx.ResultCode;
                          const status =
                            statusValue === undefined || statusValue === null
                              ? "—"
                              : String(statusValue);
                          return (
                            <tr
                              key={
                                tx.id ||
                                `${amount}-${getTransactionTime(tx)}-${idx}`
                              }
                              className="border-b last:border-b-0"
                            >
                              <td className="py-2 pr-4 text-gray-800">
                                {date
                                  ? date.toLocaleDateString()
                                  : "Unknown"}
                              </td>
                              <td className="py-2 pr-4 font-semibold text-gray-900">
                                {amount.toLocaleString()}
                              </td>
                              <td className="py-2 pr-4">
                                <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-700">
                                  {String(status).toLowerCase()}
                                </span>
                              </td>
                              <td className="py-2 pr-4 text-gray-700">
                                {tx.ConversationID ||
                                  tx.conversationId ||
                                  tx.id ||
                                  "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="py-8 text-center text-gray-500">
                    No B2C payments found for this farmer.
                  </p>
                )}
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Payment History (Current Year)
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={b2cChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="actual"
                        stroke="#0ea5e9"
                        name="Actual"
                        dot
                      />
                      <Line
                        type="monotone"
                        dataKey="projection"
                        stroke="#94a3b8"
                        strokeDasharray="5 5"
                        name="Projected"
                        dot
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Solid line shows paid months; dotted line projects expected
                  payouts for the rest of the year.
                </p>
              </div>
            </div>

            {/* Connection Info */}
            {connectedFarmerInfo && (
              <div className="mt-6 bg-cyan-50 border border-cyan-200 p-4 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-cyan-800 mb-2">
                      Connected Farmer
                    </h3>
                    {loadingConnectedInfo ? (
                      <p className="text-gray-600">
                        Loading user information...
                      </p>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-gray-700">
                          <span className="font-semibold">Name:</span>{" "}
                          {connectedFarmerInfo.name}
                        </p>
                        <p className="text-gray-700">
                          <span className="font-semibold">Email:</span>{" "}
                          {connectedFarmerInfo.email}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          This user has access to view this farmer's
                          information
                        </p>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleDetachConnectedFarmer}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition text-sm"
                  >
                    Detach
                  </button>
                </div>
              </div>
            )}
            {farmerData?.LinkedFarmer &&
              !connectedFarmerInfo &&
              !loadingConnectedInfo && (
                <div className="mt-6 bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <p className="text-yellow-800">
                    Connected farmer information could not be loaded
                  </p>
                </div>
              )}
            {canDeleteFarmer ? (
              <div className="mt-8 bg-red-50 border border-red-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-red-800 mb-2">
                  Danger zone
                </h3>
                <p className="text-sm text-red-700 mb-4">
                  Deleting this farmer will remove their validated record
                  and milk history for{" "}
                  <span className="font-semibold">{companyName}</span>.
                  This action cannot be undone.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    Delete Farmer
                  </button>
                  <button
                    onClick={() => {
                      setModeSelection(
                        farmerData?.Mode || farmer?.mode || "KCB"
                      );
                      setModeKey("");
                      setShowModeModal(true);
                    }}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition"
                  >
                    Change Payment Mode
                  </button>
                  <button
                    onClick={() => setShowBalanceAdjustmentModal(true)}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
                  >
                    Change Balance
                  </button>
                </div>
              </div>
            ) : userRole ? (
              <div className="mt-8 bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  Only a CompanyAdmin can delete farmer records.
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>
      {showMilkCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-cyan-700 flex items-center gap-2">
                <Image size={20} />
                Add Milk Card Journal
              </h3>
              <button
                onClick={() => setShowMilkCardModal(false)}
                className="text-gray-500 hover:text-gray-700"
                disabled={milkCardSaving}
              >
                <X size={20} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <select
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  value={milkCardYear}
                  onChange={(e) => setMilkCardYear(Number(e.target.value))}
                  disabled={milkCardSaving}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Month
                </label>
                <select
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  value={milkCardMonthIndex}
                  onChange={(e) => setMilkCardMonthIndex(Number(e.target.value))}
                  disabled={milkCardSaving}
                >
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { side: "front", label: "Front of Milk Card" },
                { side: "back", label: "Back of Milk Card" },
              ].map(({ side, label }) => {
                const check = milkCardChecks[side];
                const progress = milkCardUploadProgress[side] || 0;
                const file =
                  side === "front" ? milkCardFront : milkCardBack;
                return (
                  <div
                    key={side}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-gray-800">{label}</div>
                      {file && (
                        <span className="text-xs text-gray-500">
                          {file.name}
                        </span>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={(e) =>
                        handleMilkCardFileChange(
                          side,
                          e.target.files?.[0] || null
                        )
                      }
                      disabled={milkCardSaving}
                      className="w-full text-sm"
                    />
                    {check && (
                      <div
                        className={`mt-2 text-sm ${
                          check.status === "passed"
                            ? "text-green-700"
                            : check.status === "verifying"
                            ? "text-blue-700"
                            : "text-red-700"
                        }`}
                      >
                        {check.message}
                      </div>
                    )}
                    {progress > 0 && (
                      <div className="mt-2">
                        <div className="h-2 bg-gray-200 rounded">
                          <div
                            className="h-2 bg-cyan-600 rounded"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Uploading: {progress}%
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Images are saved to Firebase Storage for {companyName}/
              {farmer?.otherName}. Content moderation runs before upload to block
              inappropriate material using similar checks as Milk Journals.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowMilkCardModal(false)}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                disabled={milkCardSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMilkCard}
                className={`px-4 py-2 rounded text-white transition ${
                  milkCardSaving
                    ? "bg-cyan-300 cursor-not-allowed"
                    : "bg-cyan-600 hover:bg-cyan-700"
                }`}
                disabled={milkCardSaving}
              >
                {milkCardSaving ? "Saving..." : "Save Milk Card"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showBalanceAdjustmentModal && (
        <FarmerBalanceAdjustmentModal
          farmer={{
            ...farmer,
            ...farmerData,
          }}
          companyName={companyName}
          currentBalance={farmerData?.Balance ?? farmer?.Balance ?? 0}
          onClose={() => setShowBalanceAdjustmentModal(false)}
          onSaved={fetchFarmerDetails}
        />
      )}
      {showModeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-cyan-700">
                Update Payment Mode
              </h3>
              <button
                onClick={() => setShowModeModal(false)}
                className="text-gray-500 hover:text-gray-700"
                disabled={savingMode}
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Mode
                </label>
                <select
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  value={modeSelection}
                  onChange={(e) => setModeSelection(e.target.value)}
                  disabled={savingMode}
                >
                  <option value="KCB">KCB</option>
                  <option value="M-Pesa">M-Pesa</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Authorization Key
                </label>
                <input
                  type="text"
                  value={modeKey}
                  onChange={(e) => setModeKey(e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder={`Enter Authorization Key `}
                  disabled={savingMode}
                />
                <p className="text-xs text-gray-500 mt-1">
                Enter{" "}
                  <span className="font-semibold">
                    {farmer?.otherName}
                  </span>{" "}
                  to authorize this change.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowModeModal(false)}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                disabled={savingMode}
              >
                Cancel
              </button>
              <button
                onClick={handleModeSave}
                className={`px-4 py-2 rounded text-white transition ${
                  savingMode
                    ? "bg-cyan-300 cursor-not-allowed"
                    : "bg-cyan-600 hover:bg-cyan-700"
                }`}
                disabled={savingMode}
              >
                {savingMode ? "Saving..." : "Save Mode"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-red-700">
                Confirm farmer deletion
              </h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-500 hover:text-gray-700"
                disabled={deleting}
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Please type{" "}
              <span className="font-semibold">
                {farmer?.otherName}
              </span>{" "}
              or{" "}
              <span className="font-semibold">{companyName}</span> to
              confirm deletion.
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Type confirmation text"
              disabled={deleting}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteInput("");
                }}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteFarmer}
                className={`px-4 py-2 rounded text-white transition ${
                  deleting
                    ? "bg-red-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmerViewModal;
